import type { ReactNode } from "react";

import type {
  EditorConfig,
  LexicalEditor,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from "lexical";

import { DecoratorNode } from "lexical";

type SerializedCitationNode = Spread<
  {
    citationKey: string;
    href: string;
  },
  SerializedLexicalNode
>;

export class CitationNode extends DecoratorNode<ReactNode> {
  __citationKey: string;
  __href: string;

  static getType(): string {
    return "citation";
  }

  static clone(node: CitationNode): CitationNode {
    return new CitationNode(node.__citationKey, node.__href, node.__key);
  }

  constructor(citationKey: string, href: string, key?: NodeKey) {
    super(key);
    this.__citationKey = citationKey;
    this.__href = href;
  }

  static importJSON(serializedNode: SerializedCitationNode): CitationNode {
    const node = new CitationNode(
      serializedNode.citationKey,
      serializedNode.href,
    );
    return node;
  }

  exportJSON(): SerializedCitationNode {
    return {
      type: "citation",
      version: 1,
      citationKey: this.__citationKey,
      href: this.__href,
    };
  }

  getCitationKey(): string {
    return this.__citationKey;
  }

  getHref(): string {
    return this.__href;
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const span = document.createElement("span");
    span.setAttribute("data-citation-key", this.__citationKey);
    return span;
  }

  updateDOM(): false {
    return false;
  }

  decorate(_editor: LexicalEditor, _config: EditorConfig): ReactNode {
    return (
      <span
        className="af-inline-flex af-select-none af-items-center af-gap-1 af-rounded-md af-border af-border-input af-bg-background/60 af-px-2 af-py-0.5 af-text-xs af-font-semibold af-leading-6 af-text-accent-foreground/60"
        data-citation-key={this.__citationKey}
        data-citation-href={this.__href}
      >
        <span className="af-rounded af-bg-input af-px-1.5 af-text-interface-foreground">
          {this.__citationKey}
        </span>
      </span>
    );
  }

  isInline(): true {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }
}

export function $createCitationNode(
  citationKey: string,
  href: string,
): CitationNode {
  return new CitationNode(citationKey, href);
}

export function $isCitationNode(node: unknown): node is CitationNode {
  return node instanceof CitationNode;
}
